import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { inr } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, TriangleAlert } from "lucide-react";

const EMPTY = { name: "", sku: "", category: "", hsn: "", unit: "pcs", purchase_price: 0, selling_price: 0, stock_qty: 0, low_stock_at: 5 };

export default function Products() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => api.get("/products").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", { ...form, purchase_price: parseFloat(form.purchase_price) || 0, selling_price: parseFloat(form.selling_price) || 0, stock_qty: parseFloat(form.stock_qty) || 0, low_stock_at: parseFloat(form.low_stock_at) || 5 });
      toast.success("Product added");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const adjust = async (p) => {
    const qty = window.prompt(`Stock adjustment for "${p.name}" (current ${p.stock_qty} ${p.unit}). Enter new total:`, String(p.stock_qty));
    if (qty === null || isNaN(parseFloat(qty))) return;
    await api.patch(`/products/${p.id}`, { stock_qty: parseFloat(qty) });
    toast.success("Stock updated");
    load();
  };

  return (
    <div className="space-y-5" data-testid="products-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Products & Inventory</h1>
          <p className="text-sm text-muted-foreground">SKU catalog with live stock levels and low-stock alerts</p>
        </div>
        <Button data-testid="add-product-button" onClick={() => setOpen(true)} className="bg-primary hover:bg-primary-hover"><Plus size={16} className="mr-2" /> Add Product</Button>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]" data-testid="products-table">
          <thead className="bg-muted/60">
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-3">Product</th><th className="px-4 py-3">SKU</th><th className="px-4 py-3">Category</th>
              <th className="px-4 py-3 text-right">Purchase</th><th className="px-4 py-3 text-right">Selling</th>
              <th className="px-4 py-3 text-right">Margin</th><th className="px-4 py-3 text-right">Stock</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const low = p.stock_qty <= p.low_stock_at;
              const margin = p.purchase_price ? Math.round((p.selling_price - p.purchase_price) / p.purchase_price * 100) : 0;
              return (
                <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-t hover:bg-accent/40 transition-colors">
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{p.sku || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.category || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{inr(p.purchase_price)}</td>
                  <td className="px-4 py-3 text-right font-mono">{inr(p.selling_price)}</td>
                  <td className="px-4 py-3 text-right font-mono text-success">{margin}%</td>
                  <td className="px-4 py-3 text-right font-mono">
                    {p.stock_qty} {p.unit}
                    {low && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-danger text-danger-foreground border border-danger inline-flex items-center gap-0.5"><TriangleAlert size={10} /> Low</span>}
                  </td>
                  <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" className="h-8" data-testid={`adjust-${p.id}`} onClick={() => adjust(p)}>Adjust</Button></td>
                </tr>
              );
            })}
            {!items.length && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No products yet</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-product-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Product</DialogTitle></DialogHeader>
          <form onSubmit={add} className="space-y-3">
            <Input data-testid="product-name-input" placeholder="Product name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="product-sku-input" placeholder="SKU" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
              <Input data-testid="product-category-input" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="product-purchase-input" type="number" step="0.01" placeholder="Purchase price ₹" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} />
              <Input data-testid="product-selling-input" type="number" step="0.01" placeholder="Selling price ₹" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="product-stock-input" type="number" step="0.1" placeholder="Opening stock" value={form.stock_qty} onChange={(e) => setForm({ ...form, stock_qty: e.target.value })} />
              <Input data-testid="product-lowstock-input" type="number" placeholder="Low stock alert at" value={form.low_stock_at} onChange={(e) => setForm({ ...form, low_stock_at: e.target.value })} />
            </div>
            <Button data-testid="product-save-button" type="submit" className="w-full bg-primary hover:bg-primary-hover">Save Product</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
